import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { RuntimeConfigService } from './runtime-config.service';
import { HybridStorageService, HybridStorageKeys } from '../app/services/hybrid-storage.service';
import { Language } from '../app/models/language.model';

/**
 * Service for managing application translations and language switching
 * Wraps ngx-translate functionality with persistent language storage
 */
@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private readonly DEFAULT_LANGUAGE = 'en';
  private apiUrl: string;

  private currentLanguageSubject: BehaviorSubject<string>;
  public currentLanguage$: Observable<string>;

  private currentLanguageIdSubject: BehaviorSubject<number | null>;
  public currentLanguageId$: Observable<number | null>;

  private initPromise: Promise<void> | null = null;
  private supportedLanguages$: Observable<Language[]> | null = null;

  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService,
    private hybridStorage: HybridStorageService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl + '/Language';

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl + '/Language';
    });

    // Initialize subjects with defaults first
    this.currentLanguageSubject = new BehaviorSubject<string>(this.DEFAULT_LANGUAGE);
    this.currentLanguage$ = this.currentLanguageSubject.asObservable();

    this.currentLanguageIdSubject = new BehaviorSubject<number | null>(null);
    this.currentLanguageId$ = this.currentLanguageIdSubject.asObservable();

    // Configure translate service with default
    this.translate.setDefaultLang(this.DEFAULT_LANGUAGE);
  }

  /**
   * Initialize the translation service by loading stored language preferences
   * This should be called during app initialization
   */
  async initialize(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        const storedLanguage = await this.getStoredLanguage();
        const storedLanguageId = await this.getStoredLanguageId();

        // Update the subjects
        this.currentLanguageSubject.next(storedLanguage);
        this.currentLanguageIdSubject.next(storedLanguageId);

        // Use the translate service to set the language
        // This returns a promise that resolves when the language file is loaded
        await firstValueFrom(this.translate.use(storedLanguage));
      } catch (error) {
        console.error('Failed to initialize TranslationService:', error);
        // Fall back to default language on error
        try {
          await firstValueFrom(this.translate.use(this.DEFAULT_LANGUAGE));
        } catch (fallbackError) {
          console.error('Failed to load default language:', fallbackError);
        }
      }
    })();

    return this.initPromise;
  }

  /**
   * Get the list of supported languages from the API.
   * The list is fetched once and shared by every consumer (header language
   * switcher, message translate menu, profile, ...) for the rest of the session.
   */
  getSupportedLanguages(): Observable<Language[]> {
    if (!this.supportedLanguages$) {
      this.supportedLanguages$ = this.http.get<Language[]>(`${this.apiUrl}/GetAllLanguages`).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        // Don't cache a failure - let the next caller retry the request
        catchError(err => {
          this.supportedLanguages$ = null;
          return throwError(() => err);
        })
      );
    }
    return this.supportedLanguages$;
  }

  setSupportedLanguages(languages: Language[]): void {
    this.translate.addLangs(languages.map(lang => lang.AzureLanguageCode || this.DEFAULT_LANGUAGE));
  }

  /**
   * Get a specific language by ID
   */
  getLanguageById(languageId: number): Observable<Language> {
    return this.http.get<Language>(`${this.apiUrl}/GetLanguage/${languageId}`);
  }

  /**
   * Get current user's language preference
   */
  getUserLanguage(): Observable<Language> {
    return this.http.get<Language>(`${this.apiUrl}/GetUserLanguage`);
  }

  /**
   * Update user's language preference
   */
  updateUserLanguage(languageId: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/UpdateUserLanguage/${languageId}`,
      {}
    );
  }

  /**
   * Get the current active language
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Set the active language
   * @param lang Language code (e.g., 'en', 'es')
   */
  async setLanguage(lang: string): Promise<void> {
    this.translate.use(lang);
    await this.storeLanguage(lang);
    this.currentLanguageSubject.next(lang);
  }

  getCurrentLanguageId(): number | null {
    return this.currentLanguageIdSubject.value;
  }

  async setLanguageId(langId: number | null): Promise<void> {
    await this.storeLanguageId(langId);
    this.currentLanguageIdSubject.next(langId);
  }

  /**
   * Get translation for a key
   * @param key Translation key (e.g., 'common.save')
   * @param params Optional parameters for interpolation
   */
  instant(key: string | string[], params?: any): string | any {
    return this.translate.instant(key, params);
  }

  /**
   * Get translation as observable
   * @param key Translation key
   * @param params Optional parameters for interpolation
   */
  get(key: string | string[], params?: any): Observable<string | any> {
    return this.translate.get(key, params);
  }

  /**
   * Store the selected language in hybrid storage
   */
  private async storeLanguage(lang: string): Promise<void> {
    try {
      await this.hybridStorage.setItem(HybridStorageKeys.Translation_Language, lang);
    } catch (error) {
      console.error('Failed to store language preference:', error);
    }
  }

  /**
   * Retrieve the stored language from hybrid storage
   */
  private async getStoredLanguage(): Promise<string> {
    try {
      const stored = await this.hybridStorage.getItem<string>(HybridStorageKeys.Translation_Language);
      return stored || this.DEFAULT_LANGUAGE;
    } catch (error) {
      console.error('Failed to retrieve language preference:', error);
      return this.DEFAULT_LANGUAGE;
    }
  }

  /**
   * Store the selected language ID in hybrid storage
   */
  private async storeLanguageId(langId: number | null): Promise<void> {
    try {
      await this.hybridStorage.setItem(HybridStorageKeys.Translation_LanguageId, langId);
    } catch (error) {
      console.error('Failed to store language ID preference:', error);
    }
  }

  /**
   * Retrieve the stored language ID from hybrid storage
   */
  private async getStoredLanguageId(): Promise<number | null> {
    try {
      const stored = await this.hybridStorage.getItem<number>(HybridStorageKeys.Translation_LanguageId);
      return stored;
    } catch (error) {
      console.error('Failed to retrieve language ID preference:', error);
      return null;
    }
  }
}
